import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../../services/apiClient';
import '../../styles/fonts.css';
import '../../styles/AIChat.css';
import '../../styles/b_admin_styling.css';
import './AdminForms.css';
import './AdminTables.css';
import './AdminLayout.css';
import BButton from './ui/BButton';
import BFilterBar from './ui/BFilterBar';
import BFilterControl from './ui/BFilterControl';
import BSearchInput from './ui/BSearchInput';
import BAdminTable from './ui/BAdminTable';
import BLoading from './ui/BLoading';
import BStatusBadge from './ui/BStatusBadge';
import BKebabMenu from './ui/BKebabMenu';

const UserManagement = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [error, setError] = useState(null);
    const [businessNames, setBusinessNames] = useState({});
    const [openMenuId, setOpenMenuId] = useState(null);

    useEffect(() => {
        fetchAllUsers();
    }, []);

    const fetchAllUsers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const usersResponse = await adminAPI.getAllUsers();
            const fetchedUsers = usersResponse.data.users || [];
            setUsers(fetchedUsers);

            const adminsResponse = await adminAPI.getAllSuperAdmins();
            setAdmins(adminsResponse.data.admins || []);

            const businessIds = [...new Set(fetchedUsers.map((u) => u.business_id).filter((id) => id != null))];
            if (businessIds.length > 0) {
                try {
                    const namesResponse = await adminAPI.getBusinessNames({ ids: businessIds });
                    setBusinessNames(namesResponse.data.names || {});
                } catch (nameError) {
                    console.error('Could not fetch business names:', nameError);
                    toast.error('Could not load some business names.');
                }
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch users. Please check your connection and try again.';
            toast.error(errorMessage);
            setError(errorMessage);
            setUsers([]);
            setAdmins([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteUser = async (userId, userType, userName) => {
        try {
            if (userType === 'super_admin') {
                await adminAPI.deleteSuperAdmin(userId);
            } else {
                await adminAPI.deleteUser(userId);
            }

            toast.success(`${userType} "${userName}" deleted successfully`);
            fetchAllUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            const errorMessage = error.response?.data?.error || 'Failed to delete user';
            toast.error(errorMessage);
        }
    };

    const handleToggleUserStatus = async (user) => {
        try {
            if (user.userType === 'super_admin') {
                toast.error('Super admin status cannot be changed from this screen.');
                return;
            }

            if (user.userType === 'business_admin') {
                await adminAPI.setBusinessAdminStatus(user.id, {
                    is_active: !user.is_active,
                    reason: user.is_active ? 'manual_disabled' : undefined
                });
            } else {
                await adminAPI.toggleUserStatus(user.id);
            }

            toast.success(`${user.email} has been ${user.is_active ? 'disabled' : 'enabled'}.`);
            fetchAllUsers();
        } catch (error) {
            console.error('Error updating account status:', error);
            const errorMessage = error.response?.data?.error || 'Failed to update account status';
            toast.error(errorMessage);
        }
    };

    const handleGenerateActivationLink = async (user, sendActivationEmail = false) => {
        try {
            const response = await adminAPI.generateBusinessAdminActivationLink({
                email: user.email,
                send_activation_email: sendActivationEmail,
                delivery_mode: sendActivationEmail ? 'automated' : 'manual'
            });

            const activation = response.data?.activation;
            if (!activation?.activation_link) {
                toast.error('Activation link was not returned by the server.');
                return;
            }

            if (sendActivationEmail) {
                toast.success('Activation email sent successfully.');
            } else {
                await navigator.clipboard.writeText(activation.activation_link);
                toast.success('Activation link copied to clipboard.');
            }

            fetchAllUsers();
        } catch (error) {
            console.error('Error generating activation link:', error);
            const errorMessage = error.response?.data?.error || 'Failed to generate activation link';
            toast.error(errorMessage);
        }
    };

    const getAllUsers = () => {
        return [
            ...admins.map((admin) => ({ ...admin, userType: 'super_admin' })),
            ...users.map((user) => ({ ...user, userType: user.role || 'user' }))
        ];
    };

    const getFilteredUsers = () => {
        let filteredUsers = [];

        switch (activeTab) {
            case 'super_admins':
                filteredUsers = admins.map((admin) => ({ ...admin, userType: 'super_admin' }));
                break;
            case 'business_admins':
                filteredUsers = users.filter((user) => user.role === 'business_admin')
                    .map((user) => ({ ...user, userType: 'business_admin' }));
                break;
            case 'users':
                filteredUsers = users.filter((user) => user.role === 'user' || !user.role)
                    .map((user) => ({ ...user, userType: 'user' }));
                break;
            default:
                filteredUsers = getAllUsers();
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filteredUsers = filteredUsers.filter((user) =>
                (user.name && user.name.toLowerCase().includes(term)) ||
                (user.username && user.username.toLowerCase().includes(term)) ||
                (user.email && user.email.toLowerCase().includes(term))
            );
        }

        filteredUsers.sort((a, b) => {
            let aValue = a[sortBy];
            let bValue = b[sortBy];

            if (sortBy === 'created_at') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            }
            return aValue < bValue ? 1 : -1;
        });

        return filteredUsers;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUserStatus = (user) => {
        if (user.userType === 'super_admin') {
            return { type: 'active', label: 'Active' };
        }
        if (user.userType === 'business_admin') {
            if (user.password_setup_required) {
                return { type: 'pending', label: 'Pending Setup' };
            }
            if (!user.is_active && user.account_deactivation_reason === 'license_expired') {
                return { type: 'inactive', label: 'Expired' };
            }
            if (!user.is_active) {
                return { type: 'inactive', label: 'Disabled' };
            }
            if (user.license_expires_at && new Date(user.license_expires_at) <= new Date()) {
                return { type: 'inactive', label: 'Expired' };
            }
            return { type: 'active', label: 'Active' };
        }
        if (user.is_active === false) {
            return { type: 'inactive', label: 'Disabled' };
        }
        return { type: 'active', label: 'Active' };
    };

    const getAddButtonConfig = () => {
        switch (activeTab) {
            case 'business_admins':
                return {
                    text: 'Add Business Admin',
                    icon: 'ri-building-line',
                    onClick: () => navigate('/admin/business-admin/new')
                };
            case 'super_admins':
                return {
                    text: 'Add Super Admin',
                    icon: 'ri-user-add-line',
                    onClick: () => navigate('/admin/settings')
                };
            case 'users':
                return null;
            default:
                return {
                    text: 'Add Super Admin',
                    icon: 'ri-user-add-line',
                    onClick: () => navigate('/admin/settings')
                };
        }
    };

    const buttonConfig = getAddButtonConfig();
    const filteredUsers = getFilteredUsers();

    if (isLoading) {
        return (
            <div className="newmain-content33 user-management-page b_admin_styling-main">
                <BLoading variant="page" label="Loading users..." />
            </div>
        );
    }

    return (
        <div className="newmain-content33 user-management-page b_admin_styling-main">
            <div className="dashboard-header">
                <h1 className="chat-title page-title">User Management</h1>
                <p className="chat-subtitle">Manage all users, business admins, and super admins in the system.</p>
            </div>

            {error && (
                <div className="error-container" style={{
                    background: '#ffebee',
                    border: '1px solid #f44336',
                    borderRadius: '8px',
                    padding: '15px',
                    margin: '20px 0',
                    color: '#d32f2f'
                }}>
                    <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>Error: {error}</p>
                    <button
                        onClick={fetchAllUsers}
                        className="dashboard-button primary"
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                    >
                        <i className="ri-refresh-line"></i> Retry
                    </button>
                </div>
            )}

            <div className="user-management-controls">
                <div className="tabs-container" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <button className={`nawa2button ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
                        All Users ({getAllUsers().length})
                    </button>
                    <button className={`nawa2button ${activeTab === 'super_admins' ? 'active' : ''}`} onClick={() => setActiveTab('super_admins')}>
                        Super Admins ({admins.length})
                    </button>
                    <button className={`nawa2button ${activeTab === 'business_admins' ? 'active' : ''}`} onClick={() => setActiveTab('business_admins')}>
                        Business Admins ({users.filter((u) => u.role === 'business_admin').length})
                    </button>
                    <button className={`nawa2button ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
                        Regular Users ({users.filter((u) => u.role === 'user' || !u.role).length})
                    </button>
                </div>

                <BFilterBar>
                    <BFilterControl label="Search" htmlFor="userSearch">
                        <BSearchInput
                            id="userSearch"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search users by name, username, or email..."
                        />
                    </BFilterControl>
                    <BFilterControl label="Sort By" htmlFor="sortBy">
                        <select
                            id="sortBy"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="b_admin_styling-select b_admin_styling-select--compact"
                        >
                            <option value="created_at">Created Date</option>
                            <option value="name">Name</option>
                            <option value="email">Email</option>
                            <option value="userType">Role</option>
                        </select>
                    </BFilterControl>
                    <BFilterControl label="Order" htmlFor="sortOrder">
                        <BButton size="sm" variant="secondary" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                            {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
                        </BButton>
                    </BFilterControl>
                    {buttonConfig && (
                        <BButton onClick={buttonConfig.onClick} variant="primary" size="sm">
                            <i className={buttonConfig.icon}></i>
                            {buttonConfig.text}
                        </BButton>
                    )}
                </BFilterBar>
            </div>

            <BAdminTable headers={["Name", "Username", "Email", "Role", "Status", "Business", "Created", "Actions"]}>
                {filteredUsers.length === 0 ? (
                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: '24px' }}>No users found matching your criteria.</td></tr>
                ) : (
                    filteredUsers.map((user) => {
                        const rowKey = `${user.userType}-${user.id}`;
                        const status = getUserStatus(user);
                        const menuItems = [];

                        if (user.userType !== 'super_admin') {
                            menuItems.push({
                                label: user.is_active ? 'Disable Account' : 'Enable Account',
                                icon: user.is_active ? 'ri-user-unfollow-line' : 'ri-user-follow-line',
                                onClick: () => handleToggleUserStatus(user)
                            });
                        }

                        if (user.userType === 'business_admin') {
                            menuItems.push(
                                {
                                    label: 'Copy Activation Link',
                                    icon: 'ri-link',
                                    onClick: () => handleGenerateActivationLink(user, false)
                                },
                                {
                                    label: 'Send Activation Email',
                                    icon: 'ri-mail-send-line',
                                    onClick: () => handleGenerateActivationLink(user, true)
                                }
                            );
                        }

                        menuItems.push({
                            label: 'Delete',
                            icon: 'ri-delete-bin-line',
                            danger: true,
                            onClick: () => handleDeleteUser(user.id, user.userType, user.name || user.username)
                        });

                        return (
                            <tr key={rowKey}>
                                <td>
                                    <Link to="#" className="b_admin_styling-table__link" onClick={(e) => e.preventDefault()}>
                                        {user.name || 'N/A'}
                                    </Link>
                                </td>
                                <td>{user.username || 'N/A'}</td>
                                <td>
                                    <a href={`mailto:${user.email}`} className="b_admin_styling-table__link">{user.email}</a>
                                </td>
                                <td>{user.userType.replace('_', ' ').toUpperCase()}</td>
                                <td>
                                    <BStatusBadge type={status.type}>{status.label}</BStatusBadge>
                                </td>
                                <td>
                                    {user.business_id ? (
                                        <span title={`Business ID: ${user.business_id}`}>
                                            {businessNames[user.business_id] || `ID: ${user.business_id}`}
                                        </span>
                                    ) : (
                                        <span>N/A</span>
                                    )}
                                </td>
                                <td>{formatDate(user.created_at)}</td>
                                <td className="b_admin_styling-table__actions">
                                    <BKebabMenu
                                        isOpen={openMenuId === rowKey}
                                        onToggle={() => setOpenMenuId(openMenuId === rowKey ? null : rowKey)}
                                        items={menuItems}
                                    />
                                </td>
                            </tr>
                        );
                    })
                )}
            </BAdminTable>
        </div>
    );
};

export default UserManagement;
